// 인라인 배송지 추가 모달.
// 체크아웃 화면을 벗어나지 않고 새 배송지를 추가하기 위한 컴포넌트 (작업지시 11).
//  - 도로명 주소 검색(다음 우편번호)로 주소/우편번호를 채우고, 상세주소만 직접 입력한다.
//  - 저장 성공 시 onCreated 로 생성된 주소를 부모에 넘겨 즉시 선택되게 한다.

import { useState } from "react";
import { addressInputSchema, normalizePhoneKr, type AddressDto } from "@oo/shared";
import { createAddress } from "@/features/operations/operationsApi.ts";
import { openPostcodeSearch } from "@/features/operations/daumPostcode.ts";
import { formatPhoneKrInput } from "@/features/auth/formatPhone.ts";
import { Button } from "@/shared/ui";

interface Props {
  onClose: () => void;
  onCreated: (address: AddressDto) => void;
  // 받는 분 기본값(예: 로그인 사용자 표시 이름). 선택.
  defaultRecipientName?: string;
}

// 체크아웃 중 배송지 빠른 추가 모달.
export default function AddressQuickAddModal({ onClose, onCreated, defaultRecipientName }: Props) {
  const [label, setLabel] = useState("");
  const [recipientName, setRecipientName] = useState(defaultRecipientName ?? "");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [postalCode, setPostalCode] = useState("");
  // 기본 배송지로 지정할지 여부. 체크 시 이후 주문에서 자동 선택된다.
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // 도로명 주소 검색 팝업 → 결과를 주소/우편번호에 채운다.
  async function onSearchAddress() {
    setError(null);
    try {
      const result = await openPostcodeSearch();
      if (result) {
        setAddressLine1(result.roadAddress);
        setPostalCode(result.zonecode);
      }
    } catch {
      setError("주소 검색을 불러오지 못했습니다. 직접 입력해 주세요.");
    }
  }

  async function onSave() {
    const input = {
      label: label.trim() || null,
      recipientName: recipientName.trim(),
      recipientPhone: recipientPhone.trim(),
      addressLine1: addressLine1.trim(),
      addressLine2: addressLine2.trim() || null,
      postalCode: postalCode.trim() || null,
      isDefault,
    };
    const parsed = addressInputSchema.safeParse(input);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "입력값을 확인해 주세요.");
      return;
    }
    if (!normalizePhoneKr(parsed.data.recipientPhone)) {
      setError("휴대폰 번호 형식이 올바르지 않습니다.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await createAddress(parsed.data);
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : "주소 추가에 실패했습니다.");
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="flex max-h-[90dvh] w-full max-w-screen-sm flex-col gap-3 overflow-y-auto rounded-t-2xl bg-white p-5 pb-[calc(1.25rem+var(--safe-bottom))] sm:rounded-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">배송지 추가</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-ink-600 hover:bg-ink-100"
          >
            닫기
          </button>
        </div>

        <p className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-900">
          해당 지역명이 주소에 포함되어있어야 주문이 가능합니다.
        </p>

        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={30}
          placeholder="별칭 (예: 집, 사무실)"
          className="h-11 rounded-md border border-ink-300 px-3 text-base"
        />
        <input
          type="text"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          maxLength={30}
          placeholder="받는 분"
          className="h-11 rounded-md border border-ink-300 px-3 text-base"
        />
        <input
          type="tel"
          value={recipientPhone}
          onChange={(e) => setRecipientPhone(formatPhoneKrInput(e.target.value))}
          placeholder="연락처 010-1234-5678"
          className="h-11 rounded-md border border-ink-300 px-3 text-base tabular-nums"
        />

        {/* 도로명 주소 검색 */}
        <div className="flex gap-2">
          <input
            type="text"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            maxLength={100}
            placeholder="주소 (검색 또는 직접 입력)"
            className="h-11 min-w-0 flex-1 rounded-md border border-ink-300 px-3 text-base"
          />
          <button
            type="button"
            onClick={onSearchAddress}
            className="h-11 shrink-0 rounded-md border border-ink-400 px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100"
          >
            주소 검색
          </button>
        </div>
        <input
          type="text"
          value={addressLine2}
          onChange={(e) => setAddressLine2(e.target.value)}
          maxLength={100}
          placeholder="상세 주소 (동/호수 등)"
          className="h-11 rounded-md border border-ink-300 px-3 text-base"
        />
        {postalCode ? (
          <p className="text-xs text-ink-600">우편번호 {postalCode}</p>
        ) : null}

        {/* 기본 배송지 지정: 체크 시 다음 주문부터 이 주소가 자동 선택된다. */}
        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
            className="h-4 w-4"
          />
          기본 배송지로 지정
        </label>

        {error ? (
          <p className="rounded-md border border-danger-600/30 bg-danger-50 px-3 py-2 text-sm text-danger-700">
            {error}
          </p>
        ) : null}

        <Button type="button" fullWidth onClick={onSave} loading={saving}>
          {saving ? "저장 중..." : "이 배송지로 추가"}
        </Button>
      </div>
    </div>
  );
}
